package com.example.mystybag.login.utils

object PaymentDataValidator {
    
    /**
     * Validates IBAN (International Bank Account Number)
     * IBAN format: 2 letters (country code) + 2 digits (check digits) + up to 30 alphanumeric characters
     */
    fun isValidIban(iban: String): Boolean {
        if (iban.isBlank()) return false
        
        // Remove spaces and convert to uppercase
        val cleaned = iban.replace(" ", "").uppercase()
        
        // IBAN must be between 15 and 34 characters
        if (cleaned.length < 15 || cleaned.length > 34) return false
        
        // First 2 characters must be letters (country code)
        if (!cleaned.substring(0, 2).all { it.isLetter() }) return false
        
        // Next 2 characters must be digits (check digits)
        if (!cleaned.substring(2, 4).all { it.isDigit() }) return false
        
        // Rest must be alphanumeric
        if (!cleaned.substring(4).all { it.isLetterOrDigit() }) return false
        
        // Perform MOD-97-10 check
        return validateIbanMod97(cleaned)
    }
    
    /**
     * MOD-97-10 validation algorithm for IBAN
     */
    private fun validateIbanMod97(iban: String): Boolean {
        // Move first 4 characters to the end
        val rearranged = iban.substring(4) + iban.substring(0, 4)
        
        // Convert letters to numbers (A=10, B=11, ..., Z=35)
        val numericString = rearranged.map { char ->
            if (char.isLetter()) {
                (char.code - 'A'.code + 10).toString()
            } else {
                char.toString()
            }
        }.joinToString("")
        
        // Calculate mod 97
        var remainder = 0
        for (digit in numericString) {
            remainder = (remainder * 10 + digit.digitToInt()) % 97
        }
        
        return remainder == 1
    }
    
    /**
     * Validates BULSTAT (Bulgarian Unified Identification Code)
     * BULSTAT format: 9 or 13 digits
     */
    fun isValidBulstat(bulstat: String): Boolean {
        if (bulstat.isBlank()) return false
        
        // Remove spaces and dashes
        val cleaned = bulstat.replace(" ", "").replace("-", "")
        
        // BULSTAT must be 9 or 13 digits
        if (cleaned.length != 9 && cleaned.length != 13) return false
        
        // Must contain only digits
        if (!cleaned.all { it.isDigit() }) return false
        
        // Validate check digit for 9-digit BULSTAT
        if (cleaned.length == 9) {
            return validateBulstat9Digit(cleaned)
        }
        
        // Validate check digits for 13-digit BULSTAT
        if (cleaned.length == 13) {
            return validateBulstat13Digit(cleaned)
        }
        
        return false
    }
    
    /**
     * Validates 9-digit BULSTAT check digit
     */
    private fun validateBulstat9Digit(bulstat: String): Boolean {
        val weights = intArrayOf(1, 2, 3, 4, 5, 6, 7, 8)
        var sum = 0
        
        for (i in 0 until 8) {
            sum += bulstat[i].digitToInt() * weights[i]
        }
        
        val remainder = sum % 11
        val checkDigit = if (remainder < 10) remainder else 0
        
        return checkDigit == bulstat[8].digitToInt()
    }
    
    /**
     * Validates 13-digit BULSTAT check digits
     */
    private fun validateBulstat13Digit(bulstat: String): Boolean {
        // First 9 digits should be valid 9-digit BULSTAT
        val first9 = bulstat.substring(0, 9)
        if (!validateBulstat9Digit(first9)) return false
        
        // Validate last 4 digits (EIK)
        val weights = intArrayOf(2, 7, 3, 5)
        var sum = 0
        
        for (i in 0 until 4) {
            sum += bulstat[9 + i].digitToInt() * weights[i]
        }
        
        val remainder = sum % 11
        val checkDigit = if (remainder < 10) remainder else 0
        
        return checkDigit == bulstat[12].digitToInt()
    }
    
    /**
     * Validates VAT number (European VAT format)
     * VAT format varies by country, but generally: 2 letters (country code) + up to 12 alphanumeric characters
     */
    fun isValidVat(vat: String): Boolean {
        if (vat.isBlank()) return false
        
        // Remove spaces
        val cleaned = vat.replace(" ", "").uppercase()
        
        // VAT must be at least 4 characters (2 letter country code + at least 2 characters)
        if (cleaned.length < 4 || cleaned.length > 14) return false
        
        // First 2 characters must be letters (country code)
        if (!cleaned.substring(0, 2).all { it.isLetter() }) return false
        
        // Rest must be alphanumeric
        if (!cleaned.substring(2).all { it.isLetterOrDigit() }) return false
        
        return true
    }
    
    /**
     * Validates VAT or BULSTAT (checks both formats)
     */
    fun isValidVatOrBulstat(value: String): Boolean {
        if (value.isBlank()) return false
        return isValidVat(value) || isValidBulstat(value)
    }
}

